import React, { useEffect, useState } from 'react';
import useBundleTranslation from 'i18n';

import {
    HomePageFilterState,
    getSegmentOptions,
    SelectOption,
    getGroupByOptions,
    HomePageState,
} from 'app/home/index.functions';
import styles from './FilterPanel.styles';
import ReactSelect from 'components/common/react-select/ReactSelect';
import { Box, Button, Divider, InputAdornment, Stack, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { debounce } from 'lodash';
import { useHomeContext } from 'app/home/context';
import IconMi from 'components/common/icon/IconMi';
import { UserAuth } from 'store/auth';
import { useAppSelector } from 'store/hooks';
import { useIsAdminOrHasPrivilege } from 'hooks/useUserPrivilege';
import { Popup } from 'components/common/popup/Popup';
import GridComponent from 'components/common/grid/GridComponent';
import { filterTiles } from 'app/home/tile/TilePanel.functions';

export default function FilterPanel() {
    const { t } = useBundleTranslation(['app/home']);
    const [openPopup, setOpenPopup] = useState<boolean>(false);
    const [filteredSegmentOptions, setFilteredSegmentOptions] = React.useState<SelectOption[]>([]);
    const [filteredTagOptions, setFilteredTagOptions] = React.useState<SelectOption[]>([]);

    const ctx = useHomeContext();
    const [homePageState, setHomePageState, folderMap, data, settings, tags, tiles, refetchData] = [
        ctx.homePageState,
        ctx.setHomePageState,
        ctx.folderMap,
        ctx.data,
        ctx.homePageSettings,
        ctx.data.tags,
        ctx.tiles,
        ctx.refetchData,
    ];
    const userAuth: UserAuth = useAppSelector((state) => state.auth);

    const groupByOptions = getGroupByOptions(settings, t);
    const tagOptions = data?.tags.map((t) => {
        const result: any = {
            value: String(t.topic_id),
            label: t.topic_type_name ? `${t.topic_type_name}: ${t.full_name}` : t.full_name,
        };
        if (t.icon_type) {
            const isImageIcon = t.icon_type.startsWith('image/');
            const iconType = isImageIcon ? 'upload' : t.icon_type;
            let iconValue = t.icon;
            if (iconType == 'fa') {
                iconValue = t.fa_icon;
            } else if (iconType == 'upload') {
                iconValue = `data:${t.icon_type};base64, ${t.icon}`;
            }

            result.props = {
                icon: {
                    value: iconValue,
                    type: iconType,
                },
            };
        }
        return result;
    });
    const setFilterState = (filterState: Partial<HomePageFilterState>) => {
        setHomePageState({ ...homePageState, filterState: { ...homePageState.filterState, ...filterState } });
        return true;
    };

    const keyFilterChange = (e: any) =>
        setFilterState({ keywordFilter: e.target.value, lastFilterApplied: 'keywordFilter' });
    const debouncedOnChange = debounce(keyFilterChange, 200);
    const isForceTileView = 'Y' == settings?.parameters?.HOMEPAGE_FORCE_TILE_VIEW;

    const canManagePreferences = useIsAdminOrHasPrivilege('PRIV_PREFERENCES');
    let showSortCategory = false;
    let categoryId: number = 0;
    let categoryName: string = '';
    if (
        canManagePreferences &&
        homePageState.filterState.folderSelected.startsWith('category_') &&
        undefined !== folderMap[homePageState.filterState.folderSelected] &&
        userAuth.userInfo?.user_id == folderMap[homePageState.filterState.folderSelected].sort_order_owner_id
    ) {
        showSortCategory = true;
        categoryId = parseInt(folderMap[homePageState.filterState.folderSelected].category_id ?? '0', 10);
        categoryName = folderMap[homePageState.filterState.folderSelected].name ?? '';
    }

    const closePopup = () => {
        setOpenPopup(false);
        refetchData();
    };

    useEffect(() => {
        let filteredTilesTag = filterTiles(tiles, tags, folderMap, settings, {
            ...homePageState.filterState,
            ...{ tagFilter: [] },
        } as HomePageFilterState);

        let activeTags: { [key: string]: number } = {};
        filteredTilesTag.map((tile) => {
            tile.topics?.forEach((tag) => {
                activeTags[String(tag)] = 1;
            });
        });

        setFilteredTagOptions(
            tagOptions.filter(
                (tag) =>
                    undefined !== activeTags[tag.value] ||
                    (null !== homePageState.filterState.tagFilter &&
                        homePageState.filterState.tagFilter.includes(tag.value)),
            ),
        );

        let filteredTilesSegment = filterTiles(tiles, tags, folderMap, settings, {
            ...homePageState.filterState,
            ...{ segmentFilter: '' },
        } as HomePageFilterState);

        let activeSegmentOption =
            homePageState.filterState.segmentFilter > '' ? homePageState.filterState.segmentFilter : '';
        setFilteredSegmentOptions(getSegmentOptions(data, settings, filteredTilesSegment, activeSegmentOption, t));
    }, [homePageState, data]);

    return (
        <Stack
            direction={'row'}
            spacing={1.5}
            id={'homePageFilterPanel'}
            className={'dashboard__filter-panel'}
            sx={styles.filterPanel}
        >
            <Stack direction={'row'} className={'filter-elements'} spacing={1} sx={styles.filtersHolder}>
                <Box sx={styles.filterWrapper}>
                    <ReactSelect
                        data={groupByOptions}
                        value={homePageState.filterState.groupBy ?? null}
                        placeholder={t('group_by_folders')}
                        update={(id: string) => setFilterState({ groupBy: id, lastFilterApplied: 'groupBy' })}
                        isClearable={homePageState.filterState.groupBy !== ''}
                        name="homepage-group-by-select"
                    />
                </Box>
                <Box sx={styles.filterWrapper}>
                    <ReactSelect
                        data={filteredSegmentOptions}
                        value={homePageState.filterState.segmentFilter ?? null}
                        placeholder={t('no_filter')}
                        update={(id: string) =>
                            setFilterState({ segmentFilter: id, lastFilterApplied: 'segmentFilter' })
                        }
                        isClearable={homePageState.filterState.segmentFilter !== ''}
                        name="homepage-segment-select"
                    />
                </Box>
                <Box sx={styles.filterWrapper}>
                    <ReactSelect
                        data={filteredTagOptions}
                        placeholder={t('all_tags')}
                        value={homePageState.filterState.tagFilter || []}
                        isMulti={true}
                        isMultiOneRowMode
                        update={(id: string[]) => setFilterState({ tagFilter: id, lastFilterApplied: 'tagFilter' })}
                        name="homepage-tag-select"
                    />
                </Box>
                <Box sx={styles.filterWrapper}>
                    <TextField
                        placeholder={t('keyword_filter')}
                        onChange={keyFilterChange}
                        value={homePageState.filterState.keywordFilter || ''}
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <IconMi icon="search" fontSize="16" sx={styles.searchIcon} />
                                </InputAdornment>
                            ),
                            endAdornment: homePageState.filterState.keywordFilter ? (
                                <InputAdornment
                                    onMouseDown={() => {
                                        setFilterState({ keywordFilter: '', lastFilterApplied: '' });
                                    }}
                                    position="end"
                                    sx={styles.clearSearchIcon}
                                >
                                    <IconMi icon="times" fontSize="16" />
                                </InputAdornment>
                            ) : undefined,
                        }}
                    />
                </Box>
                {showSortCategory && (
                    <>
                        <Button
                            variant="outlined"
                            color="neutral"
                            onClick={() => setOpenPopup(true)}
                            className={'min-width--icon'}
                        >
                            <IconMi icon={'arrows-top-and-bottom'} sx={{ fontSize: 16 }} />
                        </Button>
                        {openPopup && (
                            <Popup
                                settings={{
                                    title: t('sort_elements_in_category', { categoryName }),
                                    noButtons: true,
                                }}
                                maxWidth="popupLg"
                                open={openPopup}
                                onHide={() => closePopup()}
                                onConfirm={() => closePopup()}
                            >
                                <Stack sx={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: '500px' }}>
                                    <GridComponent
                                        reloadTrigger={categoryId}
                                        uid={`sort_category_${categoryId}`}
                                        height={500}
                                        settingsUrl={`/data/editor/category/${categoryId}/sort-elements/grid`}
                                    />
                                </Stack>
                            </Popup>
                        )}
                    </>
                )}
            </Stack>

            {!isForceTileView && (
                <>
                    <Divider orientation="vertical" sx={styles.divider} />
                    <ToggleButtonGroup
                        value={homePageState.filterState.view}
                        exclusive
                        onChange={(event, viewType) => {
                            if (viewType) {
                                const newState: HomePageState = structuredClone(homePageState);
                                newState.filterState.view = viewType;
                                setHomePageState(newState);
                            }
                        }}
                    >
                        <ToggleButton value="tile" sx={styles.filterPanelViewTypeIcon}>
                            <IconMi icon={'tiles-view'} fontSize={'16'} />
                        </ToggleButton>
                        <ToggleButton value="list" sx={styles.filterPanelViewTypeIcon}>
                            <IconMi icon={'list-view'} fontSize={'16'} />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </>
            )}
        </Stack>
    );
}
